import React, { useCallback, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { DataBuilderCard } from '../../components/domain/data_builder';
import CreateFacility from './CreateFacility';
import CreateResource from './CreateResource';
import CreateEvent from './CreateEvent';
import DataBuilderComplete from './DataBuilderComplete';

/**
 * Top-level page for the 3-step Data Builder wizard.
 * Currently UI-only – no actual form submission or backend wiring.
 */
const CreateFacilityResourceEvent = () => {
  const navigate = useNavigate();
  const [currentStep, setCurrentStep] = useState(1);
  const [selectedFacility, setSelectedFacility] = useState(null);
  const [selectedResource, setSelectedResource] = useState(null);
  const [canSubmitEvent, setCanSubmitEvent] = useState(false);
  const [submitEventFn, setSubmitEventFn] = useState(null);
  const [submittedFacility, setSubmittedFacility] = useState(null);
  const [submittedResource, setSubmittedResource] = useState(null);
  const [stepFooter, setStepFooter] = useState(null);
  const totalSteps = 4;

  const resetFlow = () => {
    setCurrentStep(1);
    setSelectedFacility(null);
    setSelectedResource(null);
    setCanSubmitEvent(false);
    setSubmitEventFn(null);
    setSubmittedFacility(null);
    setSubmittedResource(null);
  };

  const handleNext = async () => {
    // Validate that required selections are made before proceeding
    if (currentStep === 1 && !selectedFacility) {
      return; // Don't proceed if no facility selected
    }
    if (currentStep === 2 && !selectedResource) {
      return; // Don't proceed if no resource selected
    }

    if (currentStep === 3) {
      // Submit event on Done
      if (!submitEventFn) return;
      const ok = await submitEventFn();
      if (ok) {
        setSubmittedFacility(selectedFacility);
        setSubmittedResource(selectedResource);
        setCurrentStep(4);
      }
      return;
    }

    if (currentStep === 4) {
      return;
    }

    setCurrentStep((prev) => (prev >= totalSteps ? 1 : prev + 1));
  };

  const handleBack = () => {
    setCurrentStep((prev) => (prev > 1 ? prev - 1 : prev));
  };

  const handleFacilitySelect = useCallback((facility) => {
    setSelectedFacility(facility);
    setSelectedResource(null);
    setCanSubmitEvent(false);
    setSubmitEventFn(null);
  }, []);

  const handleResourceSelect = useCallback((resource) => {
    setSelectedResource(resource);
    setCanSubmitEvent(false);
    setSubmitEventFn(null);
  }, []);

  const getStepTitle = () => {
    switch (currentStep) {
      case 1:
        return 'Choose Facility';
      case 2:
        return 'Choose Resource';
      case 3:
        return 'Create Event';
      case 4:
      default:
        return 'Success!';
    }
  };

  const canProceedToNextStep = () => {
    if (currentStep === 1) {
      return selectedFacility !== null;
    }
    if (currentStep === 2) {
      return selectedResource !== null;
    }
    if (currentStep === 3) {
      return selectedResource !== null && canSubmitEvent;
    }
    if (currentStep === 4) {
      return false;
    }
    return true;
  };

  const registerSubmitEvent = useCallback((fn) => {
    if (typeof fn === 'function') {
      setSubmitEventFn(() => fn);
    }
  }, []);

  const renderStepContent = () => {
    switch (currentStep) {
      case 1:
        return (
          <CreateFacility
            onFacilitySelect={handleFacilitySelect}
            selectedFacility={selectedFacility}
            onFooterChange={setStepFooter}
          />
        );
      case 2:
        return (
          <CreateResource
            selectedFacility={selectedFacility}
            onResourceSelect={handleResourceSelect}
            selectedResource={selectedResource}
            onFooterChange={setStepFooter}
          />
        );
      case 3:
        return (
          <CreateEvent
            selectedResource={selectedResource}
            registerSubmit={registerSubmitEvent}
            onCanSubmitChange={setCanSubmitEvent}
          />
        );
      case 4:
      default:
        return (
          <DataBuilderComplete
            onSameLocation={() => {
              setCanSubmitEvent(false);
              setSubmitEventFn(null);
              setCurrentStep(3);
            }}
            onDifferentLocation={resetFlow}
            onViewFacility={() => {
              const facilityId = submittedFacility?.id || selectedFacility?.id;
              if (facilityId) {
                navigate(`/facilities/${facilityId}`);
              }
            }}
            onDiscoverEvents={() => navigate('/')}
          />
        );
    }
  };

  return (
    <DataBuilderCard
      currentStep={currentStep}
      totalSteps={totalSteps}
      title={getStepTitle()}
      onNext={handleNext}
      onBack={handleBack}
      isFirstStep={currentStep === 1}
      isLastStep={currentStep === totalSteps}
      canProceed={canProceedToNextStep()}
      hideNavigation={currentStep === 4}
      footer={stepFooter}
    >
      {renderStepContent()}
    </DataBuilderCard>
  );
};

export default CreateFacilityResourceEvent;

